import './CategoryList.scss';

import { getZendeskKnowledge } from 'actions/zendesk';
import { Article } from 'api-client/models/Article';
import Button from 'components/Buttons/Button';
import ContentCard from 'components/Cards/ContentCard';
import PageHeader from 'components/Common/PageHeader';
import GlobalErrorLayout from 'components/Error/GlobalErrorLayout';
import LoadingSpinner from 'components/Loaders/LoadingSpinner';
import P from 'components/Typography/P';
import useSelector from 'hooks/useSelector';
import SearchBar from 'pages/Collection/SearchBar';
import {
    useCollectionSearchQueryChange,
    useFreeText,
    useLoadMore
} from 'pages/Collection/useCollection';
import React, { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ReactComponent as SearchEmptyStateImage } from 'static/icons/search-empty-state.svg';
import { LoadingState } from 'types/api';
import { LoadingSpinnerSize } from 'types/loadingSpinner';
import { onOpenContentItem } from 'utils/route';

const SearchQueryChangeWatcher = () => {
    useCollectionSearchQueryChange();
    return null;
};
const NoResult = () => {
    const intl = useIntl();
    return (
        <div className="no-results">
            <h2>{intl.formatMessage({ id: "collection_page.no_results.heading" })}</h2>
            <P>{intl.formatMessage({ id: "collection_page.no_results.description" })}</P>
            <SearchEmptyStateImage />
        </div>
    );
};
const SearchResults = () => {
    const loadMore = useLoadMore();
    const loading = useSelector((state) => state.collection.searchResults.loading);
    const searchResults = useSelector((state) => state.collection.searchResults.results);
    const loadingMore = useSelector((state) => state.collection.searchResults.loadingMore);
    const data = searchResults?.data as Article[];
    const hasMore = searchResults?.hasMore;
    if (loading) {
        return <LoadingSpinner relativeCenter size={LoadingSpinnerSize.large} />;
    }
    if (Array.isArray(data) && data.length === 0) {
        return <NoResult />;
    }
    if (!data) {
        return null;
    }
    const baseInfiniteScrollParentElement = (window as any).baseInfiniteScrollParentElement;
    return (
        <div className="search-results">
            <InfiniteScroll
                pageStart={0}
                loadMore={loadMore}
                className="infinite-list"
                threshold={1000}
                useWindow={false}
                getScrollParent={() => baseInfiniteScrollParentElement}
                initialLoad={false}
                hasMore={hasMore}
                loader={
                    <Button
                        key={data[0].title}
                        style={{ marginBottom: "1.5em" }}
                        size="small"
                        variant="secondary"
                        onClick={loadMore}
                        loadingSize={LoadingSpinnerSize.small}
                        loading={loadingMore}
                    >
                        Load More Articles
                    </Button>
                }
            >
                <ul className="list">
                    {data.map((article, i) => {
                        return (
                            <li className="list__item" key={`${article.id}-${i}`}>
                                <ContentCard
                                    onClick={() => onOpenContentItem(article.id)}
                                    title={article.title}
                                    tags={article.labelNames ?? []}
                                    coverImage={article.coverImage!}
                                />
                            </li>
                        );
                    })}
                </ul>
            </InfiniteScroll>
        </div>
    );
};

export const Header = ({ imageUrl, text }: { imageUrl: string; text: string }) => {
    return <PageHeader className="header" imageUrl={imageUrl} text={text} />;
};

const CategoryList = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { url } = useRouteMatch();
    const history = useHistory();
    const [freeText, onFreeTextChange] = useFreeText();

    const zendeskKnowledge = useSelector((state) => state.zendeskKnowledge);
    useEffect(() => {
        zendeskKnowledge.status !== LoadingState.loading &&
            freeText === "" &&
            dispatch(getZendeskKnowledge());
    }, []);
    if (zendeskKnowledge.status === LoadingState.failure) {
        return <GlobalErrorLayout />;
    }
    if (!zendeskKnowledge.data && zendeskKnowledge.status === LoadingState.loading) {
        return <LoadingSpinner size={LoadingSpinnerSize.large} absoluteCenter />;
    }
    if (!zendeskKnowledge.data) {
        return null;
    }

    const handleClick = (categoryId: string) => history.push(`${url}/${categoryId}`);

    /*Hide selected category until end or March 2025. More info at ticket: HRP-6907*/
    const categories = zendeskKnowledge.data.categories.filter(
        (category) => category.slug !== "compensation-rewarding-and-payroll",
    );

    return (
        <div>
            {categories && freeText === "" ? (
                <div className="category-list">
                    <div className="category-list-body">
                        <SearchBar />
                        <div className="categories-container">
                            {categories.length != 0 &&
                                categories.map((category, index) => {
                                    return (
                                        <Button
                                            key={index}
                                            size="medium"
                                            variant="secondary"
                                            onClick={() => {
                                                handleClick(category.slug!);
                                            }}
                                            className="category"
                                        >
                                            {category.title}
                                        </Button>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <SearchQueryChangeWatcher />
                    {freeText !== "" && (
                        <>
                            <div className="search-results-container">
                                <Button
                                    size="small"
                                    variant="secondary"
                                    onClick={() => onFreeTextChange("")}
                                >
                                    {intl.formatMessage({ id: "collection_page.section.go_back" })}
                                </Button>
                                <SearchBar />
                            </div>
                            <SearchResults />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default CategoryList;
